import React, { useEffect, useCallback } from 'react';
import { Modal, Form, Input, InputNumber, Select, Checkbox, Button } from 'antd';
import type { ListBuilderMeal, ListBuilderCity } from '../../types/listBuilder';
import { listBuilderEn } from '../../content/listBuilder.en';

function parseCommaList(s: string | undefined): string[] {
  if (!s || typeof s !== 'string') return [];
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}
function formatCommaList(arr: string[] | undefined): string {
  return Array.isArray(arr) ? arr.join(', ') : '';
}
/** Coerce form value to number; InputNumber may submit string when user types. */
function toNum(x: unknown): number | undefined {
  if (typeof x === 'number' && !Number.isNaN(x)) return x;
  if (x === null || x === undefined || x === '') return undefined;
  const n = Number(x);
  return Number.isNaN(n) ? undefined : n;
}

const PRICE_OPTIONS = [
  { value: 'free', label: listBuilderEn.activities.form.priceOptions.free },
  { value: '$', label: listBuilderEn.activities.form.priceOptions.$ },
  { value: '$$', label: listBuilderEn.activities.form.priceOptions['$$'] },
  { value: '$$$', label: listBuilderEn.activities.form.priceOptions['$$$'] },
  { value: '$$$$', label: listBuilderEn.activities.form.priceOptions['$$$$'] },
];
const RESERVATION_OPTIONS = [
  { value: 'required', label: listBuilderEn.meals.form.reservationOptions.required },
  { value: 'recommended', label: listBuilderEn.meals.form.reservationOptions.recommended },
  { value: 'not_needed', label: listBuilderEn.meals.form.reservationOptions.not_needed },
];

const MealFormModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (values: Partial<ListBuilderMeal> & { cityId: string }) => void;
  cities: ListBuilderCity[];
  cityId: string;
  initial?: ListBuilderMeal | null;
}> = ({ open, onClose, onSave, cities, cityId, initial }) => {
  const [form] = Form.useForm();
  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        cityId: initial?.cityId ?? cityId,
        name: initial?.name ?? '',
        cuisine: initial?.cuisine ?? '',
        description: initial?.description ?? '',
        operatingHours: initial?.operatingHours ?? '',
        priceCategory: initial?.priceCategory,
        area: initial?.area ?? '',
        url: initial?.url ?? '',
        matchScore: initial?.matchScore,
        reasoning: initial?.reasoning ?? '',
        reservationRequirement: initial?.reservationRequirement,
        kidFriendly: initial?.kidFriendly ?? false,
        tags: formatCommaList(initial?.tags),
        latitude: initial?.geocode?.latitude,
        longitude: initial?.geocode?.longitude,
        addressLine1: initial?.address?.line1 ?? '',
        addressLine2: initial?.address?.line2 ?? '',
        addressCity: initial?.address?.city ?? '',
        addressState: initial?.address?.state ?? '',
        addressPostalCode: initial?.address?.postalCode ?? '',
        addressCountry: initial?.address?.country ?? '',
        phoneNumber: initial?.phoneNumber ?? '',
      });
    }
  }, [open, initial, cityId, form]);

  const confirmClose = useCallback(() => {
    if (form.isFieldsTouched()) {
      Modal.confirm({
        title: 'Discard changes?',
        content: 'You have unsaved changes. Are you sure you want to close?',
        okText: 'Discard',
        okType: 'danger',
        cancelText: 'Keep editing',
        onOk: () => { form.resetFields(); onClose(); },
      });
    } else {
      onClose();
    }
  }, [form, onClose]);

  return (
    <Modal
      open={open}
      title={initial ? listBuilderEn.meals.editMeal : listBuilderEn.meals.addMeal}
      onCancel={confirmClose}
      footer={null}
      destroyOnClose
      width={560}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(v) => {
          const tags = parseCommaList(v.tags);
          const lat = toNum(v.latitude);
          const lng = toNum(v.longitude);
          const hasAddress = v.addressLine1 || v.addressLine2 || v.addressCity || v.addressState || v.addressPostalCode || v.addressCountry;
          onSave({
            ...v,
            cityId: v.cityId,
            tags: tags.length ? tags : undefined,
            kidFriendly: v.kidFriendly ?? false,
            geocode: lat != null && lng != null ? { latitude: lat, longitude: lng } : null,
            address: hasAddress ? {
              line1: v.addressLine1 || null,
              line2: v.addressLine2 || null,
              city: v.addressCity || null,
              state: v.addressState || null,
              postalCode: v.addressPostalCode || null,
              country: v.addressCountry || null,
            } : null,
            phoneNumber: v.phoneNumber || null,
          });
          form.resetFields();
          onClose();
        }}
      >
        <Form.Item name="cityId" label={listBuilderEn.meals.form.city} rules={[{ required: true }]}>
          <Select
            placeholder={listBuilderEn.meals.form.cityPlaceholder}
            options={cities.map((c) => ({ value: c.id, label: `${c.name}, ${c.country}` }))}
            showSearch
            filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>
        <Form.Item name="name" label={listBuilderEn.meals.form.name} rules={[{ required: true }]}>
          <Input placeholder={listBuilderEn.meals.form.namePlaceholder} />
        </Form.Item>
        <Form.Item name="phoneNumber" label={listBuilderEn.address.phoneNumber}>
          <Input placeholder={listBuilderEn.address.phoneNumberPlaceholder} />
        </Form.Item>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>{listBuilderEn.address.sectionTitle}</div>
        <Form.Item name="addressLine1" style={{ marginBottom: 4 }}>
          <Input placeholder={listBuilderEn.address.line1Placeholder} />
        </Form.Item>
        <Form.Item name="addressLine2" style={{ marginBottom: 4 }}>
          <Input placeholder={listBuilderEn.address.line2Placeholder} />
        </Form.Item>
        <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
          <Form.Item name="addressCity" style={{ flex: 1, marginBottom: 0 }}>
            <Input placeholder={listBuilderEn.address.cityPlaceholder} />
          </Form.Item>
          <Form.Item name="addressState" style={{ flex: 1, marginBottom: 0 }}>
            <Input placeholder={listBuilderEn.address.statePlaceholder} />
          </Form.Item>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <Form.Item name="addressPostalCode" style={{ flex: 1, marginBottom: 0 }}>
            <Input placeholder={listBuilderEn.address.postalCodePlaceholder} />
          </Form.Item>
          <Form.Item name="addressCountry" style={{ flex: 1, marginBottom: 0 }}>
            <Input placeholder={listBuilderEn.address.countryPlaceholder} />
          </Form.Item>
        </div>
        <Form.Item name="cuisine" label={listBuilderEn.meals.form.cuisine}>
          <Input placeholder={listBuilderEn.meals.form.cuisinePlaceholder} />
        </Form.Item>
        <Form.Item name="description" label={listBuilderEn.meals.form.description}>
          <Input.TextArea rows={2} placeholder={listBuilderEn.meals.form.descriptionPlaceholder} />
        </Form.Item>
        <Form.Item name="operatingHours" label={listBuilderEn.meals.form.operatingHours}>
          <Input placeholder={listBuilderEn.meals.form.operatingHoursPlaceholder} />
        </Form.Item>
        <Form.Item name="priceCategory" label={listBuilderEn.meals.form.priceCategory}>
          <Select options={PRICE_OPTIONS} allowClear />
        </Form.Item>
        <Form.Item name="area" label={listBuilderEn.meals.form.area}>
          <Input placeholder={listBuilderEn.meals.form.areaPlaceholder} />
        </Form.Item>
        <Form.Item name="url" label={listBuilderEn.meals.form.url}>
          <Input placeholder={listBuilderEn.meals.form.urlPlaceholder} />
        </Form.Item>
        <Form.Item name="reservationRequirement" label={listBuilderEn.meals.form.reservationRequirement}>
          <Select options={RESERVATION_OPTIONS} allowClear />
        </Form.Item>
        <Form.Item name="kidFriendly" valuePropName="checked">
          <Checkbox>{listBuilderEn.meals.form.kidFriendly}</Checkbox>
        </Form.Item>
        <Form.Item name="tags" label={listBuilderEn.meals.form.tags}>
          <Input placeholder={listBuilderEn.meals.form.tagsPlaceholder} />
        </Form.Item>
        <Form.Item label={listBuilderEn.meals.form.latitude} style={{ marginBottom: 8 }}>
          <Form.Item name="latitude" noStyle>
            <InputNumber placeholder="e.g. 48.86" style={{ width: '100%' }} />
          </Form.Item>
        </Form.Item>
        <Form.Item name="longitude" label={listBuilderEn.meals.form.longitude}>
          <InputNumber placeholder="e.g. 2.34" style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit">{listBuilderEn.buttons.save}</Button>
          <Button onClick={confirmClose} style={{ marginLeft: 8 }}>{listBuilderEn.buttons.cancel}</Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default MealFormModal;
